import {
  Datagrid,
  TextField,
  Create,
  SimpleForm,
  TextInput,
  List,
  Edit,
  FunctionField,
  Pagination,
  DeleteWithConfirmButton,
  useListContext,
  BulkDeleteWithConfirmButton,
  Toolbar,
  SaveButton,
  TopToolbar,
  useNotify,
  useRedirect,
} from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField as MuiTextField,
} from "@mui/material";
import "./styles.css";
import { useState } from "react";
import { Params } from "react-router";
import NoDataEmpty from "../NoDataEmpty";
import Add from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useCustomPermissions } from '../../hooks/usePermissions';
import { PermissionGuard } from '../../components/PermissionGuard';

const VariablePagination = (props: any) => (
  <Pagination rowsPerPageOptions={[25, 50, 100]} {...props} />
);

const SearchBar = ({
  defaultKeyword,
  onSearch,
}: {
  defaultKeyword: string;
  onSearch: (kw: string) => void;
}) => {
  const [keyword, setKeyword] = useState(defaultKeyword);

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <MuiTextField
        size="small"
        placeholder="Search keyword"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
      />
      <Button variant="contained" onClick={() => onSearch(keyword)}>
        Search
      </Button>
    </Box>
  );
};

const ActionsFunctionField = ({ agentId }: { agentId?: string }) => {
  const { hasPermission } = useCustomPermissions();

  return (
    <FunctionField
      label="Actions"
      render={(record: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGuard resource="transfer_case" action="edit">
            <Button
              component={RouterLink}
              to={`/chat-transfer-cases/${agentId}/${record.id}`}
              variant="outlined"
              size="small"
            >
              <EditIcon style={{ fontSize: 16, marginRight: 8 }} /> Edit
            </Button>
          </PermissionGuard>

          <PermissionGuard resource="transfer_case" action="delete">
            <DeleteWithConfirmButton
              record={record}
              confirmTitle="Are you sure?"
              confirmContent="You won't be able to revert this!"
              mutationMode="pessimistic"
              redirect={`/chat-transfer-cases/${agentId}`}
            />
          </PermissionGuard>
        </Box>
      )}
    />
  );
};

const TransferCaseListApi = ({ agentId }: { agentId?: string }) => {
  const { filterValues = {}, setFilters } = useListContext();
  const { hasPermission } = useCustomPermissions();

  const handleSearch = (kw: string) => {
    if (typeof setFilters === "function") {
      setFilters({ ...filterValues, keyword: kw }, null);
    }
  };
  return (
    <Card sx={{ m: 2 }}>
      <CardHeader title="Manage Data" />
      <CardContent>
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
          <SearchBar
            defaultKeyword={filterValues.keyword || ""}
            onSearch={handleSearch}
          />
        </Box>
        <Datagrid
          rowClick={hasPermission('transfer_case', 'edit') ? (id: any) => `/chat-transfer-cases/${agentId}/${id}` : false}
          bulkActionButtons={
            hasPermission('transfer_case', 'delete') ? (
              <BulkDeleteWithConfirmButton
                confirmTitle="Are you sure?"
                confirmContent="This will permanently delete the selected variables."
                mutationMode="pessimistic"
              />
            ) : false
          }
        >
          <TextField source="value" cellClassName="column-value-case" />
          {(hasPermission('transfer_case', 'edit') || hasPermission('transfer_case', 'delete')) && (
            <FunctionField
              label="Actions"
              render={(record: any) => <ActionsFunctionField agentId={agentId} />}
              cellClassName="column-actions"
            />
          )}
        </Datagrid>
      </CardContent>
    </Card>
  );
};

const ListActions: React.FC<{ agentId?: string }> = ({ agentId }) => {
  const navigate = useNavigate();
  return (
    <TopToolbar>
      <PermissionGuard resource="transfer_case" action="create">
        <Button
          variant="contained"
          onClick={() => navigate(`/chat-transfer-cases/${agentId ?? ""}/create`)}
        >
          <Add fontSize="small" sx={{ mr: 0.5 }} /> Create
        </Button>
      </PermissionGuard>
    </TopToolbar>
  );
};

export const TransferCaseList = (props: any) => {
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();
  return (
    <List
      {...props}
      filters={[]}
      component="div"
      perPage={25}
      pagination={<VariablePagination />}
      actions={<ListActions agentId={agentId} />}
      empty={<NoDataEmpty entityKey="chat-transfer-cases" agentId={agentId} />}
    >
      <TransferCaseListApi agentId={agentId} />
    </List>
  );
};

export const TransferCaseCreate = (props: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();
  return (
    <Create
      {...props}
      component="div"
      transform={(data: any) => ({ ...transformEdit(data), agent_id: agentId })}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Updated successfully", { type: "success" });
          redirect(`/chat-transfer-cases/${agentId || ""}`);
        },
        onError: (error: any) =>
          notify(`Error: ${error?.message}`, { type: "error" }),
      }}
    >
      <Card sx={{ m: 2 }}>
        <CardHeader title="Add Data" />
        <CardContent>
          <SimpleForm>
            <TextInput source="value" fullWidth />
          </SimpleForm>
        </CardContent>
      </Card>
    </Create>
  );
};
const transformEdit = (data: any) => ({
  ...data,
  value: data.value?.trim(),
});
const CustomToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);
export const TransferCaseEdit = (props: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();
  return (
    <Edit
      {...props}
      component="div"
      transform={(data: any) => ({ ...transformEdit(data), agent_id: agentId })}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Updated successfully", { type: "success" });
          redirect(`/chat-transfer-cases/${agentId || ""}`);
        },
        onError: (error: any) =>
          notify(`Error: ${error?.message}`, { type: "error" }),
      }}
    >
      <Card sx={{ m: 2 }}>
        <CardHeader title="Edit Data" />
        <CardContent>
          <SimpleForm toolbar={<CustomToolbar />}>
            <TextInput source="value" fullWidth />
          </SimpleForm>
        </CardContent>
      </Card>
    </Edit>
  );
};
